/**
 * ForgotPasswordPage — Demande de réinitialisation de mot de passe.
 *
 * Refonte R5 (mai 2026) : LogoMark + Card glass + Alert avec icône
 * Logique métier 100% préservée : forgotPassword + écran confirm, anti-énum, 429 handling.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Field, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth-context';
import { ApiException } from '@/lib/api';
import { ArrowLeft, Mail, KeyRound, AlertCircle } from 'lucide-react';

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      // Le serveur retourne TOUJOURS 200 sur cette route (anti-énum).
      // Donc une erreur ici = vrai problème (réseau, rate-limit applicatif).
      if (err instanceof ApiException && err.status === 429) {
        setError(
          'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.'
        );
      } else {
        setError('Une erreur est survenue. Veuillez réessayer dans un instant.');
      }
    } finally {
      setLoading(false);
    }
  }

  // ---------- ÉCRAN DE CONFIRMATION ----------
  if (submitted) {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <LogoMark icon={Mail} accent="tech" size="lg" aria-label="Email envoyé" />
          </div>
          <div>
            <SectionEyebrow as="div">Demande envoyée</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Vérifiez votre boîte mail
            </h1>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Si un compte existe avec l'adresse <strong className="text-ink">{email}</strong>,
            vous recevrez dans quelques minutes un email contenant un lien pour réinitialiser
            votre mot de passe.
          </p>
          <p className="text-xs text-ink-subtle leading-relaxed">
            Pensez à vérifier vos courriers indésirables. Le lien est valable 1 heure.
          </p>
          <div className="pt-2">
            <Link to="/connexion">
              <Button variant="secondary">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // ---------- FORMULAIRE ----------
  return (
    <PublicLayout maxWidth="sm">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center">
            <LogoMark icon={KeyRound} accent="tech" size="lg" aria-label="Mot de passe oublié" />
          </div>
          <SectionEyebrow as="div" className="mt-4">Mot de passe</SectionEyebrow>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
            Mot de passe oublié&nbsp;?
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            Indiquez votre adresse email, nous vous enverrons un lien pour en choisir un nouveau.
          </p>
        </div>

        <Card variant="glass" padded>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="error" icon={AlertCircle}>
                {error}
              </Alert>
            )}

            <Field label="Adresse email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="vous@exemple.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="h-12 sm:h-11"
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              Envoyer le lien
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-ink-muted">
          <Link
            to="/connexion"
            className="inline-flex items-center gap-1 font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md px-1"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Retour à la connexion
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
